import { StateCreator } from "zustand";
import { supabase } from "../../../../lib/supabase";
import { useCentralStore } from ".";
import { Logger } from "../../../../lib/logger/Logger";
import { showNotification } from "./selectors";
import { Region } from "../../types/enums";
import { RegionConfig, REGION_OPTIONS } from "../../types/region.types";
import { RoundToType } from "../Jobs/utils";
import { State } from "./types";
import { SupabaseWriteTableEnum } from "../../../../lib/supabase/supabaseTypes";

export interface RegionsSlice {
	// Regions methods
	loadRegions: () => Promise<void>;
	addRegion: (regionCode: Region) => void;
	updateRegion: (index: number, field: keyof RegionConfig, value: any) => void;
	removeRegion: (index: number) => void;
	saveRegions: (defaultRegionId: number | null) => Promise<boolean>;

	// Auto-save methods
	addRegionAndSave: (regionCode: Region) => Promise<void>;
	updateRegionAndSave: (index: number, field: keyof RegionConfig, value: any) => Promise<void>;
	removeRegionAndSave: (index: number) => Promise<void>;
	saveRegion: (region: RegionConfig) => Promise<void>;
	deleteRegionFromDb: (regionId: number) => Promise<void>;
}

export const createRegionsSlice: StateCreator<
	State & RegionsSlice,
	[],
	[],
	RegionsSlice
> = (set, get) => ({
	loadRegions: async () => {
		const { organization } = get();
		if (!organization?.id) {
			Logger.warn("No organization ID found when loading regions");
			return;
		}

		set({ regionsLoading: true });

		try {
			const { data, error } = await supabase
				.from("activated_regions")
				.select("*")
				.eq("organization_id", organization.id)
				.order("region", { ascending: true });

			if (error) {
				Logger.error("Error loading regions: " + error.message);
				showNotification({
					message: "Fehler beim Laden der Regionen",
					type: "error",
				});
				return;
			}

			if (data && data.length > 0) {
				const regions: RegionConfig[] = data.map(region => ({
					id: region.id,
					region: region.region as Region,
					is_mwst_eust: region.is_mwst_eust,
					mwst_number: region.mwst_number,
					mwst_percentage: region.mwst_percentage,
					currency_text: region.currency_text,
					round_to: region.round_to,
				}));
				set({ activatedRegions: regions });
			} else {
				set({ activatedRegions: [] });
			}
		} catch (error) {
			Logger.error("Error loading regions: " + String(error));
			showNotification({
				message: "Fehler beim Laden der Regionen",
				type: "error",
			});
		} finally {
			set({ regionsLoading: false });
		}
	},

	addRegion: (regionCode: Region) => {
		const { activatedRegions } = get();

		// Check if region already exists
		if (activatedRegions.some(r => r.region === regionCode)) {
			showNotification({
				message: "Diese Region ist bereits aktiviert",
				type: "error",
			});
			return;
		}

		const regionOption = REGION_OPTIONS.find(r => r.value === regionCode);
		if (!regionOption) {
			Logger.error("Region option not found: " + regionCode);
			return;
		}

		const newRegion: RegionConfig = {
			region: regionCode,
			is_mwst_eust: false,
			mwst_number: null,
			mwst_percentage: regionOption.defaultVat,
			currency_text: regionOption.currencyText,
			round_to: regionOption.roundTo,
		};

		set({ activatedRegions: [...activatedRegions, newRegion] });
	},

	updateRegion: (index: number, field: keyof RegionConfig, value: any) => {
		const { activatedRegions } = get();
		const updated = [...activatedRegions];
		updated[index] = {
			...updated[index],
			[field]: value,
		};
		set({ activatedRegions: updated });
	},

	removeRegion: (index: number) => {
		const { activatedRegions } = get();
		set({ activatedRegions: activatedRegions.filter((_, i) => i !== index) });
	},

	// Auto-save methods
	addRegionAndSave: async (regionCode: Region) => {
		const { organization, activatedRegions } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return;
		}

		// Check if region already exists
		if (activatedRegions.some(r => r.region === regionCode)) {
			showNotification({
				message: "Diese Region ist bereits aktiviert",
				type: "error",
			});
			return;
		}

		const regionOption = REGION_OPTIONS.find(r => r.value === regionCode);
		if (!regionOption) {
			Logger.error("Region option not found: " + regionCode);
			return;
		}

		const newRegion = {
			organization_id: organization.id,
			region: regionCode,
			is_mwst_eust: false,
			mwst_number: null,
			mwst_percentage: regionOption.defaultVat,
			currency_text: regionOption.currencyText,
			round_to: regionOption.roundTo,
		};

		try {
			const { data, error } = await supabase
				.from("activated_regions")
				.insert(newRegion)
				.select()
				.single();

			if (error) {
				Logger.error("Error adding region: " + error.message);
				showNotification({
					message: "Fehler beim Hinzufügen der Region",
					type: "error",
				});
				return;
			}

			if (data) {
				const regionConfig: RegionConfig = {
					id: data.id,
					region: data.region as Region,
					is_mwst_eust: data.is_mwst_eust,
					mwst_number: data.mwst_number,
					mwst_percentage: data.mwst_percentage,
					currency_text: data.currency_text,
					round_to: data.round_to,
				};

				set({ activatedRegions: [...activatedRegions, regionConfig] });

				// If it's the first region, make it default
				if (activatedRegions.length === 0 && data.id) {
					await useCentralStore.getState().updateDefaultRegion(data.id);
				}

				showNotification({
					message: "Region erfolgreich hinzugefügt",
					type: "success",
				});
			}
		} catch (error) {
			Logger.error("Error adding region: " + String(error));
			showNotification({
				message: "Fehler beim Hinzufügen der Region",
				type: "error",
			});
		}
	},

	updateRegionAndSave: async (index: number, field: keyof RegionConfig, value: any) => {
		const { activatedRegions, organization } = get();
		const region = activatedRegions[index];

		if (!region.id || !organization?.id) {
			Logger.error("Cannot update region without ID");
			return;
		}

		// Update local state immediately for responsiveness
		const updated = [...activatedRegions];
		updated[index] = {
			...updated[index],
			[field]: value,
		};
		set({ activatedRegions: updated });

		// Prepare update data
		const updateData: any = {};
		if (field === 'is_mwst_eust') {
			updateData.is_mwst_eust = value;
			// Clear mwst_number if EUSt is disabled
			if (!value) {
				updateData.mwst_number = null;
			}
		} else if (field === 'mwst_number') {
			updateData.mwst_number = value || null;
		} else if (field === 'mwst_percentage') {
			updateData.mwst_percentage = value;
		} else if (field === 'currency_text') {
			updateData.currency_text = value || null;
		} else if (field === 'round_to') {
			updateData.round_to = value;
		}

		try {
			const { error } = await supabase
				.from("activated_regions")
				.update(updateData)
				.eq("id", region.id)
				.eq("organization_id", organization.id);

			if (error) {
				// Revert on error
				set({ activatedRegions });
				Logger.error("Error updating region: " + error.message);
				showNotification({
					message: "Fehler beim Aktualisieren der Region",
					type: "error",
				});
			} else {
				showNotification({
					message: "Region erfolgreich aktualisiert",
					type: "success",
				});
			}
		} catch (error) {
			// Revert on error
			set({ activatedRegions });
			Logger.error("Error updating region: " + String(error));
			showNotification({
				message: "Fehler beim Aktualisieren der Region",
				type: "error",
			});
		}
	},

	removeRegionAndSave: async (index: number) => {
		const { activatedRegions, organization } = get();
		const regionToRemove = activatedRegions[index];

		if (!regionToRemove.id || !organization?.id) {
			// If no ID, just remove from local state (not saved yet)
			set({ activatedRegions: activatedRegions.filter((_, i) => i !== index) });
			return;
		}

		// Check if it's the default region
		if (regionToRemove.id === organization.default_activated_region_id) {
			// Update default to null first
			await useCentralStore.getState().updateDefaultRegion(null);
		}

		try {
			const { error } = await supabase
				.from("activated_regions")
				.delete()
				.eq("id", regionToRemove.id)
				.eq("organization_id", organization.id);

			if (error) {
				Logger.error("Error removing region: " + error.message);
				showNotification({
					message: "Fehler beim Entfernen der Region",
					type: "error",
				});
				return;
			}

			// Update local state
			set({ activatedRegions: activatedRegions.filter((_, i) => i !== index) });

			showNotification({
				message: "Region erfolgreich entfernt",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error removing region: " + String(error));
			showNotification({
				message: "Fehler beim Entfernen der Region",
				type: "error",
			});
		}
	},

	saveRegion: async (region: RegionConfig) => {
		const { organization } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when saving region");
			return;
		}

		const data = {
			id: region.id,
			organization_id: organization.id,
			region: region.region,
			is_mwst_eust: region.is_mwst_eust,
			mwst_number: region.is_mwst_eust ? region.mwst_number : null,
			mwst_percentage: region.mwst_percentage,
			currency_text: region.currency_text,
			round_to: region.round_to,
		};

		try {
			const { error } = await supabase
				.from("activated_regions")
				.upsert(data, {
					onConflict: "organization_id,region",
				});

			if (error) {
				Logger.error("Error saving region: " + error.message);
				showNotification({
					message: "Fehler beim Speichern der Region",
					type: "error",
				});
			}
		} catch (error) {
			Logger.error("Error saving region: " + String(error));
			showNotification({
				message: "Fehler beim Speichern der Region",
				type: "error",
			});
		}
	},

	deleteRegionFromDb: async (regionId: number) => {
		const { organization } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when deleting region");
			return;
		}

		try {
			const { error } = await supabase
				.from("activated_regions")
				.delete()
				.eq("id", regionId)
				.eq("organization_id", organization.id);

			if (error) {
				Logger.error("Error deleting region: " + error.message);
				showNotification({
					message: "Fehler beim Löschen der Region",
					type: "error",
				});
			}
		} catch (error) {
			Logger.error("Error deleting region: " + String(error));
			showNotification({
				message: "Fehler beim Löschen der Region",
				type: "error",
			});
		}
	},

	saveRegions: async (defaultRegionId: number | null) => {
		const { organization, activatedRegions } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return false;
		}

		try {
			// First, get all existing regions to identify which ones to delete
			const { data: existingRegions, error: fetchError } = await supabase
				.from("activated_regions")
				.select("id, region")
				.eq("organization_id", organization.id);

			if (fetchError) {
				Logger.error("Error fetching existing regions: " + fetchError.message);
				showNotification({
					message: "Fehler beim Speichern der Regionen",
					type: "error",
				});
				return false;
			}

			// Delete regions that are no longer in the activated list
			const regionsToDelete = existingRegions?.filter(
				existing => !activatedRegions.some(activated => activated.region === existing.region)
			) || [];

			if (regionsToDelete.length > 0) {
				const { error: deleteError } = await supabase
					.from("activated_regions")
					.delete()
					.in("id", regionsToDelete.map(r => r.id));

				if (deleteError) {
					Logger.error("Error deleting regions: " + deleteError.message);
					showNotification({
						message: "Fehler beim Löschen der Regionen",
						type: "error",
					});
					return false;
				}
			}

			// Upsert the activated regions
			let updatedRegions: RegionConfig[] = [];
			if (activatedRegions.length > 0) {
				const organizationId = organization.id ?? "";
				if (!organizationId) {
					Logger.error("No organization ID when saving regions");
					return false;
				}

				const upsertData = activatedRegions.map(region => ({
					id: region.id,
					organization_id: organizationId,
					region: region.region,
					is_mwst_eust: region.is_mwst_eust,
					mwst_number: region.is_mwst_eust ? region.mwst_number : null,
					mwst_percentage: region.mwst_percentage,
					currency_text: region.currency_text,
					round_to: region.round_to,
				}));

				const { data, error } = await supabase
					.from("activated_regions")
					.upsert(upsertData, {
						onConflict: "organization_id,region",
					})
					.select();

				if (error) {
					Logger.error("Error saving regions: " + error.message);
					showNotification({
						message: "Fehler beim Speichern der Regionen",
						type: "error",
					});
					return false;
				}

				// Update local state with returned data (includes IDs for new records)
				if (data) {
					updatedRegions = data.map(region => ({
						id: region.id,
						region: region.region as Region,
						is_mwst_eust: region.is_mwst_eust,
						mwst_number: region.mwst_number,
						mwst_percentage: region.mwst_percentage,
						currency_text: region.currency_text,
						round_to: region.round_to,
					}));
					set({ activatedRegions: updatedRegions });
				}
			}

			// Update the default region in organizations table
			let finalDefaultId = defaultRegionId;

			// If the current default was deleted or doesn't exist in updated regions, clear it
			if (finalDefaultId && !updatedRegions.some(r => r.id === finalDefaultId)) {
				finalDefaultId = null;
			}

			// If there's only one region and no default is set, make it the default
			if (!finalDefaultId && updatedRegions.length === 1 && updatedRegions[0].id) {
				finalDefaultId = updatedRegions[0].id;
			}

			// Update organization with the default region
			const { error: orgError } = await supabase
				.from(SupabaseWriteTableEnum.ORGANIZATIONS)
				.update({ default_activated_region_id: finalDefaultId })
				.eq("id", organization.id);

			if (orgError) {
				Logger.error("Error updating default region: " + orgError.message);
				showNotification({
					message: "Fehler beim Speichern der Standard-Region",
					type: "error",
				});
				return false;
			}

			// Refresh organization data in the store
			await useCentralStore.getState().getOrganization();

			showNotification({
				message: "Regionen erfolgreich gespeichert",
				type: "success",
			});

			return true;
		} catch (error) {
			Logger.error("Error saving regions: " + String(error));
			showNotification({
				message: "Fehler beim Speichern der Regionen",
				type: "error",
			});
			return false;
		}
	},
});